/**
 * Outbox strings. Extracted out of `en.ts` (which is over the file-length ratchet and may only
 * shrink) and mounted there as `outbox`, mirroring the `enInbox.ts` pattern.
 */
export const enOutbox = {
	cancelSchedule: {
		action: "Cancel",
		title: "Cancel scheduled message",
		confirmation: "Are you sure you want to cancel this scheduled message? This cannot be undone.",
		cancelled: "Cancelled",
		success: "Scheduled message cancelled",
		error: "Could not cancel the scheduled message",
	},
	scheduled: "Scheduled",
	scheduledFor: "Scheduled for {{time}}",
	/** A manual follow-up parked behind a LinkedIn invitation has no send time yet (PREC-4220). */
	waitingForInvite_one: "Sends {{count}} day after the invitation is accepted",
	waitingForInvite_other: "Sends {{count}} days after the invitation is accepted",
	waitingForInviteNow: "Sends as soon as the invitation is accepted",
	/** Edit one manual scheduled message in place (PREC-4220). */
	editSchedule: {
		action: "Edit",
		title: "Edit scheduled message",
		subjectLabel: "Subject",
		subjectPlaceholder: "Enter email subject...",
		messageLabel: "Message",
		sendTimeLabel: "Send",
		delayLabel: "After the invite is accepted",
		noteCounter: "{{count}}/{{limit}} characters",
		noteTooLong: "A LinkedIn invitation note is at most {{limit}} characters.",
		emptyMessage: "The message must not be empty.",
		success: "Scheduled message updated",
		error: "Could not update the scheduled message",
	},
	/** Pause / resume one manual follow-up (PREC-2861). */
	pauseSchedule: {
		pauseAction: "Pause",
		resumeAction: "Resume",
		pauseTitle: "Pause scheduled message",
		resumeTitle: "Resume scheduled message",
		pauseConfirmation:
			"The message will not be sent until you resume it. Its scheduled time is kept.",
		resumeConfirmationFuture: "The message will be sent at {{date}}.",
		resumeConfirmationPast:
			"The scheduled time ({{date}}) has already passed, so the message will be sent as soon as possible after you resume it.",
		resumeConfirmationNow: "The message will be sent as soon as possible after you resume it.",
		resumeConfirmationWaitingForInvite:
			"The message will be sent {{count}} days after the LinkedIn invite is accepted.",
		paused: "Paused",
		pausedWithTime: "Paused · was scheduled for {{time}}",
		pausedWaitingForInvite_one: "Paused · will send {{count}} day after the invitation is accepted",
		pausedWaitingForInvite_other:
			"Paused · will send {{count}} days after the invitation is accepted",
		pausedSuccess: "Scheduled message paused",
		resumedSuccess: "Scheduled message resumed",
		pauseError: "Could not pause the scheduled message",
		resumeError: "Could not resume the scheduled message",
	},
	/** Send one manual scheduled message right away (PREC-3103). */
	sendNowSchedule: {
		action: "Send now",
		title: "Send scheduled message now",
		confirmation:
			"The message will be sent now instead of at {{date}}. It usually goes out within a minute.",
		confirmationNow: "The message will be sent now. It usually goes out within a minute.",
		confirmationPaused:
			"This message is paused. Sending it now ends the pause and sends it within a minute.",
		success: "Message is being sent",
		error: "Could not send the scheduled message now",
	},
	no_sender: "Not yet assigned to a specific sender",
	linkedinInviteNode: "Send LinkedIn Invite",
	sent_linkedinInviteNode: "Sent LinkedIn Invite",
	linkedinNode: "Send LinkedIn Message",
	emailNode: "Send email",
	linkedinVisitNode: "Trigger LinkedIn Visit",
	sent_linkedinVisitNode: "Triggered LinkedIn Visit",
	sent_linkedinNode: "Sent LinkedIn Message",
	sent_emailNode: "Sent email",
	linkedinInmailNode: "Send Linkedin InMail",
	sent_linkedinInmailNode: "Sent Linkedin InMail",
	linkedinProfileVisit: "Trigger LinkedIn Profile Visit",
	sent_linkedinProfileVisit: "Triggered LinkedIn Profile Visit",
	mail_sent: "Reply from Inbox - Email",
	mail_scheduled: "Scheduled from Inbox - Email",
	message_sent: "Reply from Inbox - LinkedIn",
	message_scheduled: "Scheduled from Inbox - LinkedIn",
	follow_up_reply_1_email: "Follow up reply 1 from Inbox - Email",
	follow_up_reply_1_linkedin: "Follow up reply 1 from Inbox - LinkedIn",
	follow_up_reply_2_email: "Follow up reply 2 from Inbox - Email",
	follow_up_reply_2_linkedin: "Follow up reply 2 from Inbox - LinkedIn",
	changeSenderTitle: "Change Sender",
	changeSenderConfirmation: "Are you sure you want to change the sender for this schedule?",
	changeSenderWarning:
		"This lead already has non-silent steps executed (e.g. emails or LinkedIn messages sent). Changing the sender will cause the campaign to restart from the beginning for this lead.",

	/** Outbox status tabs (PREC-3850). */
	statusTabs: {
		all: "All",
		scheduled: "Scheduled",
		inProgress: "In progress",
		ended: "Ended",
		paused: "Paused",
		error: "Error",
	},
	allInclude: {
		hint: "Include in All",
		note: "Scheduled is off by default — nothing has been sent yet.",
	},
	sortByOptions: {
		lastMessageSent: "Last message sent",
	},
	filters: {
		campaign: "Campaign",
		allSenders: "All",
		meSender: "Me ({{name}})",
		/** A campaign without a name, by its list (PREC-4291). */
		unnamedCampaignOfList: "{{list}} campaign",
		/** A selected campaign the options cannot place, e.g. deleted (PREC-4291). */
		campaignFromLink: "Campaign from link",
		source: {
			all: "All",
			chat: "Chat",
			campaign: "Campaign",
		},
	},

	/** Compact lead card (PREC-3850). */
	card: {
		chatOutreach: "Chat outreach",
	},
	statusChips: {
		notStarted: "Not started",
		inProgress: "In progress",
		completed: "Completed",
		paused: "Paused",
		replied: "Replied",
		toReview: "To review",
		archived: "Archived",
	},
	hold: {
		paused: "Paused",
		stopped: "Stopped",
		stoppedTooltip:
			"Someone at this company answered, so the outreach to their colleagues was stopped. Stopped on {{date}}.",
		away: "Away until {{date}}",
		awayTooltip:
			"This lead replied with an out-of-office notice, so the next step was moved to their return on {{date}} instead of going out to an unattended mailbox.",
		awayEnded: "Away — ended",
		awayEndedTooltip:
			"This lead replied with an out-of-office notice announcing a return on {{date}} — too far out to keep the sequence waiting, so their outreach was ended.",
	},
	steps: {
		stepOf: "Step {{current}} of {{total}}",
		sentOf: "{{sent}} of {{total}} sent",
	},

	/** Group headers of the Outbox leads column, keyed by the card's derived category. */
	dividers: {
		paused: "Paused",
		sentToday: "Sent today",
		sentYesterday: "Sent yesterday",
		sentUnknown: "Sent",
		nothingSentYet: "Nothing sent yet",
		sentOn: "Sent on {{date}}",
		scheduled: "Scheduled",
		scheduledToday: "Scheduled for today",
		scheduledTomorrow: "Scheduled for tomorrow",
		scheduledNextWeek: "Scheduled for next week",
		scheduledLater: "Scheduled for later",
		scheduledOn: "Scheduled for {{date}}",
		/** An assigned lead with no dated step yet, e.g. a first-step condition still to run. */
		waitingToStart: "Waiting to start",
	},

	/**
	 * Why a step failed, keyed by the API's `ScheduleFailureCategory`. `label` is the tag/node text
	 * ({{step}} is the step name, e.g. "Send LinkedIn Invite"); `message` says what happened and
	 * `actionHint` what to do about it — both shown in the tag's tooltip.
	 */
	failure: {
		stepFallback: "This step",
		DISCONNECTED: {
			label: "{{step}} failed",
			message: "The sending account was disconnected, so this step could not be sent.",
			actionHint:
				"Reconnect the account under Personal settings → Connected Accounts to resume the campaign.",
		},
		RATE_LIMITED: {
			label: "{{step}} failed",
			message: "LinkedIn's sending limit for this account was reached.",
			actionHint: "Sending resumes automatically once the limit resets — no action needed.",
		},
		INSUFFICIENT_PERMISSIONS: {
			label: "{{step}} failed",
			message: "The sending account isn't allowed to perform this action.",
			actionHint: "Check the account's LinkedIn plan and permissions.",
		},
		NOT_CONNECTED: {
			label: "{{step}} not sent",
			message:
				"This person hasn't accepted your LinkedIn invitation yet, so the message couldn't be sent.",
			actionHint:
				"Wait for them to accept the invitation and send the message again — nothing about the account or its plan is wrong.",
		},
		NO_INMAIL_CREDITS: {
			label: "{{step}} failed",
			message: "The LinkedIn account has no InMail credits left.",
			actionHint: "Top up InMail credits in LinkedIn, or use a different step for this lead.",
		},
		NOT_ENOUGH_CREDITS: {
			label: "{{step}} failed",
			message:
				"Your workspace is out of credits, so this step's AI snippet couldn't be written and the step wasn't sent.",
			actionHint:
				"Top up credits under Company Settings → Plans & Billing. The lead's next step still runs at its usual time.",
		},
		MISSING_COLUMN_VALUE: {
			label: "{{step}} not sent",
			message:
				"A variable or column in this step's message had no value for this lead, so the step was not sent with the placeholder in it.",
			messageWithColumns:
				"Missing value for {{columns}}, so the step was not sent with the placeholder in it.",
			actionHint:
				"Fill in the value for this lead (for a category, score the lead), or edit the step's message, then send the step again.",
			testSend:
				"Missing value for {{columns}} for this lead, so the test was not sent. Fill in the value, or test with another lead.",
		},
		CONTENT_TOO_LONG: {
			label: "{{step}} failed",
			message: "The message exceeded LinkedIn's character limit once the variables were filled in.",
			actionHint: "Shorten the step's message in the campaign.",
		},
		RECIPIENT_UNREACHABLE: {
			label: "{{step}} not delivered",
			message: "This person can't be reached on LinkedIn — their profile is restricted or gone.",
			actionHint: "Try reaching them by email instead.",
		},
		ALREADY_INVITED: {
			label: "{{step}} skipped",
			message: "An invitation to this person already exists.",
			actionHint: "No action needed — the campaign continues with the next step.",
		},
		MISSING_EMAIL: {
			label: "{{step}} skipped",
			message: "This lead has no email address, so no email could be sent.",
			actionHint: "Enrich the lead to find an email, or reach out on LinkedIn instead.",
		},
		EMAIL_BOUNCED: {
			label: "{{step}} skipped",
			message:
				"An earlier email to this address bounced, so no more emails are sent to it — another bounce would hurt your mailbox's reputation.",
			actionHint:
				"If you have a working address for this lead, enter it under Edit contact info. Otherwise, reach out on LinkedIn instead.",
		},
		MISSING_LINKEDIN_URL: {
			label: "{{step}} skipped",
			message: "This lead has no LinkedIn profile, so no LinkedIn step can run.",
			actionHint: "Enrich the lead to find their profile, or reach out by email instead.",
		},
		ALREADY_CONNECTED: {
			label: "{{step}} stopped",
			message:
				"This person is already connected to the sender on LinkedIn, so no invite was sent and the campaign stopped for them.",
			actionHint:
				'Send them a message from the Inbox, or turn off "Stop the campaign for leads who are already connected" on the step to let the campaign continue.',
		},
		BLACKLISTED: {
			label: "{{step}} blocked",
			message: "This lead is on your blacklist, so the send was blocked.",
			actionHint: "Remove them from the blacklist if you want to contact them.",
			matched: "Matched the Do Not Contact entry {{value}}.",
			matchedCompetitor: "Matched the competitor entry {{value}}.",
			matchedUnsubscribed: "{{value}} unsubscribed.",
		},
		LIST_PERSON_DELETED: {
			label: "{{step}} skipped",
			message: "This lead was removed from the list before the step ran.",
			actionHint: "No action needed.",
		},
		TRANSIENT: {
			label: "{{step}} failed",
			message: "This step couldn't be sent after several attempts.",
			actionHint: "We're looking into it — contact support if it keeps happening.",
		},
		TIMED_OUT: {
			label: "{{step}} may not have been sent",
			message:
				"LinkedIn or the mail provider timed out while this step was being sent, so it may or may not have gone out.",
			actionHint:
				"Check the conversation before sending it again. It was not retried automatically, so it cannot arrive twice.",
		},
		UNKNOWN: {
			label: "{{step}} failed",
			message: "This step couldn't be sent.",
			actionHint: "We're looking into it — contact support if it keeps happening.",
		},
	},
};
